import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  CheckCircle2,
  Receipt,
  Package2,
  User,
  X,
} from 'lucide-react';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Chip } from '@/components/ui/Chip';
import { Modal } from '@/components/ui/Modal';
import { Input } from '@/components/ui/Input';
import { formatIDR, formatNumber } from '@/lib/format';
import { useEvent } from '@/hooks/useEvents';
import { useMyDrafts, useSaveDraft } from '@/hooks/useDrafts';
import { useEventAllocations } from '@/hooks/useEventAllocations';
import { CustomerPicker } from '@/components/customers/CustomerPicker';
import { CustomerForm } from '@/components/customers/CustomerForm';
import { ProductGrid } from '@/components/pos/ProductGrid';
import { Cart } from '@/components/pos/Cart';
import { HelpButton } from '@/components/tour/HelpButton';
import { DraftsManager } from '@/components/pos/DraftsManager';
import { POSMenu } from '@/components/pos/POSMenu';
import { EventTransactionsList } from '@/components/pos/EventTransactionsList';
import { EventStockList } from '@/components/pos/EventStockList';
import { useCart, selectCartItemCount, expandLines, type CartLotOption } from '@/store/cart';
import { usePosShiftStore } from '@/store/posShift';
import { usePosCheckout } from '@/store/posCheckout';
import { useEventPromos } from '@/hooks/useEventPromos';
import { calculateTotals, resolveAutoPromos } from '@/lib/pricing';
import type { TransactionDraft, TransactionItem } from '@/types/domain';

/**
 * A draft stores one line per (product, batch) — the shape the invoice has —
 * so a cart line with a split becomes several draft lines, and an unsplit one
 * a single line with no batch, to be resolved on load.
 */
function draftItems(items: ReturnType<typeof useCart.getState>['items']): TransactionItem[] {
  const out: TransactionItem[] = [];
  for (const i of items) {
    const byId = new Map(i.lotOptions.map((o) => [o.allocationId, o]));
    const lots = i.lots.filter((l) => l.qty > 0);
    if (lots.length === 0) {
      out.push({ productId: i.productId, name: i.name, price: i.price, qty: i.qty, returnedQty: 0,
        subtotal: i.price * i.qty, unit: i.unit, promoDiscount: 0 });
      continue;
    }
    for (const l of lots) {
      const o = byId.get(l.allocationId);
      out.push({
        productId: i.productId, allocationId: l.allocationId, name: i.name, price: i.price, qty: l.qty,
        returnedQty: 0, subtotal: i.price * l.qty, lotNumber: o?.lotNumber, expiryDate: o?.expiryDate,
        unit: o?.unit ?? i.unit, promoDiscount: 0,
      });
    }
  }
  return out;
}

/** How long the "Transaksi tersimpan" toast stays on screen before
 *  auto-dismissing. Long enough to read the invoice number, short
 *  enough to not block the next sale. */
const SUCCESS_TOAST_MS = 5000;

export default function POS() {
  const navigate = useNavigate();
  const shift = usePosShiftStore();
  const { data: activeEvent } = useEvent(shift.eventId);
  const { data: promos } = useEventPromos(shift.eventId);

  // Local UI state
  // Discount, tax, fulfilment, customer, promos and the loaded draft live in a
  // store shared with the review step (/pos/cart).
  const {
    discountType, discountValue, taxEnabled, taxRate, fulfillment, customer,
    promoIds, loadedDraftId, lastSuccess: success,
  } = usePosCheckout();
  const setDiscount = usePosCheckout((s) => s.setDiscount);
  const setTaxEnabled = usePosCheckout((s) => s.setTaxEnabled);
  const setFulfillment = usePosCheckout((s) => s.setFulfillment);
  const setCustomer = usePosCheckout((s) => s.setCustomer);
  const setLoadedDraftId = usePosCheckout((s) => s.setLoadedDraftId);
  const setSuccess = usePosCheckout((s) => s.setLastSuccess);
  const resetSale = usePosCheckout((s) => s.resetSale);
  const [customerFormOpen, setCustomerFormOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [transactionsOpen, setTransactionsOpen] = useState(false);
  const [stockOpen, setStockOpen] = useState(false);

  // Drafts UI
  const [draftsOpen, setDraftsOpen] = useState(false);
  const [draftLabelModal, setDraftLabelModal] = useState<{ label: string; editable: boolean } | null>(null);
  // Which saved draft the cart currently came from, if any. Without this the
  // draft outlived its own sale: it was never deleted after charging, and
  // saving again inserted a second copy instead of updating the first.
  const [draftNotice, setDraftNotice] = useState<string | null>(null);
  const successTimerRef = useRef<number | null>(null);

  const cartItems = useCart((s) => s.items);
  const cartCount = useCart(selectCartItemCount);
  const addItem = useCart((s) => s.addItem);
  const clear = useCart((s) => s.clear);

  // Drafts — per-cashier via useMyDrafts(cashierId). We always scope by
  // shift.cashierId so a draft list survives a page refresh (Firestore
  // is the source of truth; this hook refetches on mount).
  const { data: drafts } = useMyDrafts(shift.cashierId || null);
  const draftCount = drafts?.length ?? 0;
  const saveDraft = useSaveDraft();
  // Live lot stock, to rebuild maxStock when a draft is loaded — a draft saved
  // an hour ago knows nothing about what has sold since.
  const { data: eventAllocations } = useEventAllocations(shift.eventId || null);

  const totals = useMemo(
    () =>
      calculateTotals({
        items: cartItems,
        promoLines: expandLines(cartItems),
        // Same resolution as the review step: bogo by itself, the rest as chosen.
        promos: resolveAutoPromos(
          expandLines(cartItems),
          (promos ?? [])
            .filter((p) => p.isActive)
            .map((p) => ({ id: p.id, kind: p.kind, name: p.name, config: p.config, items: p.items })),
          promoIds,
        ),
        discountType,
        discountValue,
        taxEnabled,
        taxRate,
      }),
    [cartItems, promos, promoIds, discountType, discountValue, taxEnabled, taxRate],
  );

  useEffect(() => {
    if (success && cartCount > 0) setSuccess(null);
  }, [success, cartCount, setSuccess]);

  // Auto-dismiss the success toast after SUCCESS_TOAST_MS. The toast
  // gets re-set by handleSuccess, so we always clear the previous timer
  // first to avoid an early dismiss.
  useEffect(() => {
    if (!success) return;
    if (successTimerRef.current) window.clearTimeout(successTimerRef.current);
    successTimerRef.current = window.setTimeout(() => {
      setSuccess(null);
      successTimerRef.current = null;
    }, SUCCESS_TOAST_MS);
    return () => {
      if (successTimerRef.current) {
        window.clearTimeout(successTimerRef.current);
        successTimerRef.current = null;
      }
    };
  }, [success, setSuccess]);

  // Reset sale state when the event changes (shift swap)
  useEffect(() => {
    resetSale();
    setSuccess(null);
    setError(null);
  }, [shift.eventId, resetSale, setSuccess]);

  const canCharge = useMemo(() => {
    if (!shift.eventId) return false;
    if (!activeEvent || activeEvent.status !== 'active') return false;
    if (cartItems.length === 0) return false;
    if (totals.grandTotal <= 0) return false;
    return true;
  }, [shift.eventId, activeEvent, cartItems.length, totals.grandTotal]);

  // Step two: lots, promos, totals, payment.
  const handleCharge = useCallback(() => {
    if (!canCharge) return;
    setError(null);
    navigate('/pos/cart');
  }, [canCharge, navigate]);

  const handleEndShift = useCallback(() => {
    shift.endShift();
    useCart.getState().clear();
    resetSale();
  }, [shift, resetSale]);

  const eventIsClosed = activeEvent && activeEvent.status !== 'active';

  // ==== Drafts ====

  const defaultDraftLabel = useMemo(() => {
    if (customer?.name) return customer.name;
    const now = new Date();
    const hh = String(now.getHours()).padStart(2, '0');
    const mm = String(now.getMinutes()).padStart(2, '0');
    return `Draft @ ${hh}:${mm}`;
  }, [customer?.name]);

  const handleSaveDraftClick = useCallback(() => {
    if (cartItems.length === 0) return;
    if (!shift.eventId || !shift.cashierId) {
      setError('Shift belum dimulai.');
      return;
    }
    setError(null);
    setDraftLabelModal({ label: defaultDraftLabel, editable: true });
  }, [cartItems.length, shift.eventId, shift.cashierId, defaultDraftLabel]);

  const handleConfirmSaveDraft = useCallback(async () => {
    if (!draftLabelModal) return;
    if (!shift.eventId || !shift.cashierId) return;
    if (cartItems.length === 0) return;
    setError(null);
    try {
      await saveDraft.mutateAsync({
        // Re-saving a loaded draft updates it in place; without the id every
        // save inserted a fresh row and the list filled with duplicates.
        id: loadedDraftId ?? undefined,
        cashierId: shift.cashierId,
        cashierName: shift.cashierName,
        eventId: shift.eventId,
        eventName: activeEvent?.name ?? '',
        eventCode: activeEvent?.code ?? '',
        label: draftLabelModal.label.trim() || defaultDraftLabel,
        items: draftItems(cartItems),
        subtotal: totals.subtotal,
        discountType,
        discountValue,
        discountAmount: totals.discountAmount,
        taxEnabled,
        taxRate: taxEnabled ? taxRate : 0,
        taxAmount: totals.taxAmount,
        grandTotal: totals.grandTotal,
        fulfillment,
        customerId: customer?.id ?? null,
        customerSnapshot: customer
          ? {
              name: customer.name,
              phone: customer.phone,
              company: customer.company ?? null,
              email: customer.email ?? null,
            }
          : null,
        notes: null,
      });
      // Clear the cart + sale state after a successful save so the
      // cashier is in a clean state for the next sale.
      clear();
      resetSale();
      setDraftLabelModal(null);
    } catch (e) {
      setError((e as Error).message);
    }
  }, [
    draftLabelModal,
    defaultDraftLabel,
    shift.eventId,
    shift.cashierId,
    shift.cashierName,
    activeEvent?.name,
    activeEvent?.code,
    cartItems,
    totals,
    discountType,
    discountValue,
    taxEnabled,
    taxRate,
    fulfillment,
    customer,
    clear,
    resetSale,
    saveDraft,
    loadedDraftId,
  ]);

  const handleLoadDraft = useCallback(
    (draft: TransactionDraft) => {
      // Reset all in-memory cart state, then rehydrate from the draft.
      clear();
      resetSale();
      setDraftNotice(null);

      // Resolve every line against the lots on the event RIGHT NOW. A draft
      // is a parked sale, and stock has moved since it was parked: the lot it
      // named may be sold out or closed, and its qty may exceed what is left.
      // Options and ceilings come from the live allocations, never the draft.
      const byProduct = new Map<string, CartLotOption[]>();
      for (const a of eventAllocations ?? []) {
        if (a.closedAt) continue;
        const remaining = Math.max(0, a.qtyIn - a.qtySold);
        if (remaining <= 0) continue;
        const list = byProduct.get(a.productId) ?? [];
        list.push({
          allocationId: a.id,
          lotNumber: a.lotNumber || undefined,
          expiryDate: a.expiryDate,
          unit: a.unit,
          remaining,
        });
        byProduct.set(a.productId, list);
      }
      for (const list of byProduct.values()) {
        list.sort((x, y) => (x.expiryDate ?? Infinity) - (y.expiryDate ?? Infinity));
      }
      const priceByProduct = new Map((eventAllocations ?? []).map((a) => [a.productId, a.productPrice]));

      // One cart line per product; the draft's batch lines become its split.
      const grouped = new Map<string, { name: string; qty: number; lots: { allocationId: string; qty: number }[] }>();
      for (const item of draft.items) {
        const g = grouped.get(item.productId) ?? { name: item.name, qty: 0, lots: [] };
        g.qty += item.qty;
        if (item.allocationId) g.lots.push({ allocationId: item.allocationId, qty: item.qty });
        grouped.set(item.productId, g);
      }
      const skipped: string[] = [];
      for (const [productId, g] of grouped) {
        const options = byProduct.get(productId) ?? [];
        const available = options.reduce((s, o) => s + o.remaining, 0);
        if (available <= 0) {
          skipped.push(`${g.name} (habis)`);
          continue;
        }
        addItem({
          productId,
          name: g.name,
          // Charged at the allocation's price, as create_transaction will.
          price: priceByProduct.get(productId) ?? 0,
          unit: options[0]?.unit,
          lotOptions: options,
          qty: Math.min(g.qty, available),
          lots: g.lots,
        });
        if (g.qty > available) skipped.push(`${g.name} (sisa ${available})`);
      }
      if (skipped.length > 0) {
        setDraftNotice(
          `Sebagian draft tidak bisa dimuat utuh (${skipped.slice(0, 3).join(', ')}${skipped.length > 3 ? '…' : ''}) — stoknya habis atau berkurang. Cek lot di langkah keranjang.`,
        );
      }

      setLoadedDraftId(draft.id);
      setDiscount(draft.discountType, draft.discountValue);
      setTaxEnabled(draft.taxEnabled);
      setFulfillment(draft.fulfillment);
      if (draft.customerId && draft.customerSnapshot) {
        setCustomer({
          id: draft.customerId,
          name: draft.customerSnapshot.name,
          phone: draft.customerSnapshot.phone,
          company: draft.customerSnapshot.company ?? undefined,
          email: draft.customerSnapshot.email ?? undefined,
          isActive: true,
          createdAt: 0,
          updatedAt: 0,
        });
      }
      setDraftsOpen(false);
    },
    [clear, resetSale, addItem, eventAllocations, setLoadedDraftId, setDiscount, setTaxEnabled, setFulfillment, setCustomer],
  );

  return (
    // Fixed to the viewport so the cart column is height-bound and its item
    // list scrolls internally, keeping the pay button in view. With
    // min-h-screen the column grew with every item and the footer walked off
    // the bottom of the page.
    //
    // Gated on viewport HEIGHT as well as width. Pinning the page on a short
    // screen — a 768px laptop, or any screen at 125% browser zoom — leaves the
    // bottom of the cart clipped with no way to scroll to it, which is worse
    // than the problem it solves. Below that height the page scrolls again.
    <div className="min-h-screen [@media(min-width:1024px)_and_(min-height:700px)]:h-screen [@media(min-width:1024px)_and_(min-height:700px)]:overflow-hidden bg-canvas flex flex-col">
      {/* Top bar — minimal, shows shift context */}
      <header className="px-4 sm:px-6 lg:px-8 py-2.5 border-b border-line bg-surface flex items-center justify-between gap-4 shrink-0">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-9 h-9 rounded bg-primary/10 text-primary flex items-center justify-center shrink-0">
            <Receipt size={18} />
          </div>
          <div className="min-w-0">
            <p className="text-overline uppercase text-ink-subtle font-medium leading-none">
              POS Shift
            </p>
            <p className="text-body font-display font-semibold text-ink truncate leading-tight">
              {activeEvent?.name ?? '…'}
              {activeEvent?.code && (
                <code className="ml-1.5 text-caption font-mono text-ink-subtle">
                  {activeEvent.code}
                </code>
              )}
            </p>
          </div>
          {activeEvent && activeEvent.status === 'active' && (
            <Chip variant="active">
              <span className="w-1.5 h-1.5 rounded-full bg-success animate-pulse" />
              Live
            </Chip>
          )}
          {eventIsClosed && <Chip variant="default">Closed</Chip>}
        </div>

        <div className="flex items-center gap-1.5 shrink-0">
          <HelpButton />
          {/* Inline quick-access actions. The most common operations
              (Transaksi, Stok) live here so the cashier doesn't have
              to dig into a menu. The "..." overflow goes to POSMenu. */}
          <Button
            variant="ghost"
            size="md"
            onClick={() => setTransactionsOpen(true)}
            disabled={!shift.eventId}
            leftIcon={<Receipt size={14} />}
            title="Lihat semua transaksi di event ini"
            className="hidden sm:inline-flex"
          >
            Transaksi
          </Button>
          <Button
            variant="ghost"
            size="md"
            onClick={() => setStockOpen(true)}
            disabled={!shift.eventId}
            leftIcon={<Package2 size={14} />}
            title="Lihat stock real-time di event ini"
            className="hidden sm:inline-flex"
          >
            Stok
          </Button>
          <div className="hidden md:block w-px h-6 bg-line mx-1" />
          <div className="hidden sm:flex items-center gap-2 px-3 py-1.5 rounded bg-canvas border border-line">
            <User size={14} className="text-ink-subtle" />
            <span className="text-small text-ink">{shift.cashierName}</span>
          </div>
          <POSMenu
            cashierName={shift.cashierName}
            eventName={activeEvent?.name ?? '(tidak ada event)'}
            shiftStartedAt={shift.startedAt}
            shiftTransactions={shift.transactions}
            shiftTotalSales={shift.totalSales}
            onEndShift={handleEndShift}
          />
        </div>
      </header>

      {eventIsClosed && (
        <div className="px-4 sm:px-6 lg:px-8 py-2 bg-danger/5 border-b border-danger/20 text-caption text-danger text-center">
          Event ini sudah ditutup. Transaksi tidak bisa dibuat. Akhiri shift dan pilih event lain.
        </div>
      )}

      <div className="flex-1 min-h-0 flex flex-col">
        <div className="px-4 sm:px-6 lg:px-8 pt-3 pb-2 shrink-0">
          {success && (
            <Card
              padding="md"
              className={
                success.paymentTerms === 'tempo'
                  ? 'mb-3 border-warning/30 bg-warning/5'
                  : 'mb-3 border-success/30 bg-success/5'
              }
            >
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-3 min-w-0">
                  <span
                    className={
                      success.paymentTerms === 'tempo'
                        ? 'w-9 h-9 rounded-full bg-warning/20 text-warning flex items-center justify-center shrink-0'
                        : 'w-9 h-9 rounded-full bg-success/20 text-success flex items-center justify-center shrink-0'
                    }
                  >
                    <CheckCircle2 size={18} />
                  </span>
                  <div className="min-w-0">
                    <p className="text-small font-medium text-ink">
                      {success.paymentTerms === 'tempo'
                        ? 'Piutang tersimpan'
                        : 'Transaksi tersimpan'}
                    </p>
                    <p className="text-caption text-ink-subtle font-mono">
                      {success.invoiceNumber}
                    </p>
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setSuccess(null)}
                  aria-label="Dismiss"
                >
                  <X size={14} />
                </Button>
              </div>
            </Card>
          )}

          {draftNotice && (
            <Card padding="md" className="mb-3 border-warning/30 bg-warning/5">
              <div className="flex items-center justify-between gap-3">
                <p className="text-small text-warning">{draftNotice}</p>
                <Button variant="ghost" size="sm" onClick={() => setDraftNotice(null)} aria-label="Tutup">
                  <X size={14} />
                </Button>
              </div>
            </Card>
          )}

          {error && (
            <Card padding="md" className="mb-3 border-danger/30 bg-danger/5">
              <div className="flex items-center justify-between gap-3">
                <p className="text-small text-danger">{error}</p>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setError(null)}
                  aria-label="Dismiss error"
                >
                  <X size={14} />
                </Button>
              </div>
            </Card>
          )}

          {/* Width-matched to the product grid column (1fr + 16px gap + 400px
              cart). The picker's dropdown is positioned to this card's edges,
              so a full-width card hung the open list over the top of the cart
              — the first click there only dismissed the list. */}
          <Card data-tour="pos-customer" padding="sm" className="mb-3 overflow-visible relative z-20 lg:mr-[416px]">
            <CustomerPicker
              value={customer?.id ?? null}
              onSelect={(c) => setCustomer(c)}
              onAddNew={() => setCustomerFormOpen(true)}
              placeholder="Pilih customer (opsional) — ketik untuk mencari…"
              id="pos-customer-picker"
            />
          </Card>
        </div>

        <div className="flex-1 min-h-0 px-4 sm:px-6 lg:px-8 pb-4">
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_400px] gap-4 h-full min-h-0">
            <Card data-tour="pos-products" padding="md" className="min-h-0 flex flex-col">
              <ProductGrid eventId={shift.eventId} groupByCategory />
            </Card>
            <div data-tour="pos-cart" className="min-h-0">
              <Cart
                discountType={discountType}
                discountValue={discountValue}
                onDiscountChange={(next) => setDiscount(next.type, next.value)}
                taxEnabled={taxEnabled}
                taxRate={taxRate}
                onTaxEnabledChange={setTaxEnabled}
                fulfillment={fulfillment}
                onFulfillmentChange={setFulfillment}
                onCharge={handleCharge}
                onSaveDraft={handleSaveDraftClick}
                savingDraft={saveDraft.isPending}
                draftCount={draftCount}
                onOpenDrafts={() => setDraftsOpen(true)}
                canCharge={canCharge}
                chargeLabel={(total) => `Lanjut · ${formatIDR(total)}`}
              />
            </div>
          </div>
        </div>
      </div>

      <CustomerForm
        open={customerFormOpen}
        onClose={() => setCustomerFormOpen(false)}
      />

      <DraftsManager
        open={draftsOpen}
        onClose={() => setDraftsOpen(false)}
        cashierId={shift.cashierId}
        onLoad={handleLoadDraft}
        currentCartItemCount={cartCount}
      />

      {/* Save-draft label prompt */}
      <Modal
        open={!!draftLabelModal}
        onClose={() => setDraftLabelModal(null)}
        title="Simpan draft"
        description="Cart akan disimpan. Customer boleh ganti, draft ini hanya milik kamu."
        size="md"
        footer={
          <>
            <Button
              variant="ghost"
              onClick={() => setDraftLabelModal(null)}
              disabled={saveDraft.isPending}
            >
              Batal
            </Button>
            <Button
              variant="primary"
              onClick={handleConfirmSaveDraft}
              loading={saveDraft.isPending}
              disabled={cartItems.length === 0}
            >
              Simpan
            </Button>
          </>
        }
      >
        <Input
          label="Label draft"
          value={draftLabelModal?.label ?? ''}
          onChange={(e) =>
            setDraftLabelModal((s) => (s ? { ...s, label: e.target.value } : s))
          }
          placeholder="cth: Customer Ani, atau Draft @ 14:30"
          hint="Default: nama customer, atau jam simpan kalau tanpa customer."
          autoFocus
        />
        <div className="mt-3 p-3 rounded bg-canvas border border-line space-y-1">
          <p className="text-caption text-ink-subtle">
            <span className="text-overline uppercase">Cart yang akan disimpan</span>
          </p>
          <p className="text-small text-ink">
            {formatNumber(cartCount)} item · {formatIDR(totals.grandTotal)}
            {customer && (
              <>
                {' · '}
                <span className="text-ink-subtle">untuk {customer.name}</span>
              </>
            )}
          </p>
        </div>
      </Modal>

      <EventTransactionsList
        open={transactionsOpen}
        onClose={() => setTransactionsOpen(false)}
        eventId={shift.eventId ?? ''}
        eventName={activeEvent?.name}
      />

      <EventStockList
        open={stockOpen}
        onClose={() => setStockOpen(false)}
        eventId={shift.eventId ?? ''}
        eventName={activeEvent?.name}
      />
    </div>
  );
}
